import axios from 'axios';
import dayjs from 'dayjs';
import { get, sortBy } from 'lodash';
import log from 'chameleon/ui-stack/utilities/log';
import { getSubmissions } from 'chameleon/src/api/submissions';

import {
  FETCH_ASSIGNMENT_DATA_SUCCESS,
  FETCH_ASSIGNMENT_DATA_FAILED,
  FETCH_ASSIGNMENT_DATA_START,
  FETCH_ASSIGNMENT_STUDENTS_STATUS_FAILED,
  FETCH_ASSIGNMENT_STUDENTS_STATUS_START,
  FETCH_ASSIGNMENT_STUDENTS_STATUS_SUCCESS,
  REMOVE_ASSIGNMENT_STUDENTS,
} from 'legacy/src/constants/types';

const getAssignment = (assignmentId, params) =>
  axios.get(`${FYM_BACKEND_URL}/assessments/api/assignments/${assignmentId}/`, {
    params,
  });

const getPaperscorerDetails = (assignmentId) => {
  const make_request = () =>
    axios.get(
      `${FYM_BACKEND_PAPERSCORER_API_URL}/paperscorer/assignment/${assignmentId}`,
    );
  // on local only, if the ps-api request fails, don't prevent the page from loading.
  if (process.env.ENV === 'dev') {
    return make_request().catch((e) => {
      log('error', e);
      log(
        'warning',
        'ps-api request failed, returning no sessions to reduce need to run ps-api locally.',
      );
      return { data: { sessions: [] } };
    });
  }
  return make_request();
};

export const fetchAssignmentData =
  (assignmentId, params = {}) =>
  async (dispatch) => {
    dispatch({
      type: FETCH_ASSIGNMENT_DATA_START,
    });
    const reqs = [
      getAssignment(assignmentId, params),
      getSubmissions(assignmentId),
      getPaperscorerDetails(assignmentId),
    ];
    const [assignmentResponse, submissionResponse, paperscorerResponse] =
      await axios.all(reqs).catch((error) => log('error', error));
    try {
      const { submissions } = submissionResponse.data;
      const associatedResources = {};
      assignmentResponse.data.associated_resources.forEach(
        ({ resource_type, resource_url }) => {
          associatedResources[resource_type] = resource_url;
        },
      );
      const assignmentData = assignmentResponse.data;
      if (assignmentData.assessment_group) {
        assignmentData.due_at = dayjs(assignmentData.starts_at).add(
          assignmentData.timer,
          'minute',
        );
      }

      // remove unused properties this should have been removed as part of
      // TI-671 but the endpoint is too interwoven with the rest of the codebase
      // this is the compromise for now to reduce the scope of that work
      delete assignmentData.students;
      delete assignmentData.section_students;
      delete assignmentData.full_assignemt_students_ids;

      const payload = {
        assignment: {
          ...assignmentData,
          sub_group_students: assignmentData.sub_group_students.map(
            (student) => student.id,
          ),
        },
        submissionData: {
          submissions: sortBy(submissions, ['last_name', 'first_name']),
        },
        associatedResources,
        paperscorerData: paperscorerResponse.data,
      };
      dispatch({
        type: FETCH_ASSIGNMENT_DATA_SUCCESS,
        payload,
      });
    } catch (error) {
      dispatch({
        type: FETCH_ASSIGNMENT_DATA_FAILED,
        payload: { errorMessage: get(error, 'message') },
      });
      log('error', error);
    }
  };

export const fetchAssignmentStudentStatusData =
  (assignmentId, studentIds, assignmentStatus, isUpdating = false) =>
  async (dispatch) => {
    dispatch({
      type: FETCH_ASSIGNMENT_STUDENTS_STATUS_START,
      payload: { isFetching: !isUpdating },
    });
    try {
      const assignmentStudentsStatus = await axios.get(
        `${FYM_BACKEND_URL}/assessments/api/assignments/${assignmentId}/students/status`,
        {
          params: {
            studentIds: studentIds.join(','),
            assignmentStatus,
          },
        },
      );
      dispatch({
        type: FETCH_ASSIGNMENT_STUDENTS_STATUS_SUCCESS,
        payload: { [assignmentId]: assignmentStudentsStatus.data },
      });
    } catch (error) {
      dispatch({
        type: FETCH_ASSIGNMENT_STUDENTS_STATUS_FAILED,
        payload: { errorMessage: String(error) },
      });
    }
  };

export const removeAssignmentStudentStatusData =
  (assignmentIds) => (dispatch) =>
    dispatch({
      type: REMOVE_ASSIGNMENT_STUDENTS,
      payload: { assignmentIds },
    });
